import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connect } from 'react-redux';
import { Card, CardContent } from '@ferris/react/components/cards';
import {
    isContentMessagingUpdatesEnabled,
    isValuePropBannerActive
} from '../../../utils/ExperimentsUtil';
import ExperimentUtil from '../../../utils/ExperimentUtil';
import TranslationUtil from '../../../utils/TranslationUtil';
import HeroV2 from './HeroV2';
import HeroV1 from './HeroV1';
import { HERO_IMAGE_V2 } from '../../../../server/utils/Cms/constants';
import ValuePropBanner from './ValuePropBanner';
import HeroV4 from './HeroV4';

export const Hero = (props) => {
    const {
        image,
        logoInfo,
        translations,
        copy,
        isFrboFlow,
        children,
        heroV2Props,
        valuePropBannerProps,
        isRebranded
    } = props;
    const heroCopy = copy;
    const { url, alt } = isFrboFlow ? HERO_IMAGE_V2 : image;
    const mobileUrl = isFrboFlow ? HERO_IMAGE_V2.mobileSrc : image.url;

    const clBrandingTitle = translations.getTranslation('cl.lyp.branding.hero.title1');
    const clBrandingSubtitle = translations.getTranslation('cl.lyp.branding.hero.subtitle');

    const heroV4Props = isFrboFlow ? {
        copy: heroV2Props.copy,
        titleList: heroV2Props.titleList,
        titleSuffix: heroV2Props.titleSuffix
    } : {
        copy: clBrandingSubtitle,
        titleList: [clBrandingTitle]
    };

    const heroImgs = {
        cl: {
            desktop: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/ulx-hero-desktop.webp',
            mobile: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/ulx-hero-mobile.webp'
        },
        vr: {
            desktop: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/Desktop-header-no-scrim_new_branding_V2.webp',
            mobile: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/Mobile-header-no-scrim-new_branding_V2.webp'
        }
    };

    const heroImgToUse = isFrboFlow ? heroImgs.vr : heroImgs.cl;
    const rebrandedAlt = 'View of guest walking past the check-in desk at Maritime Hotel';
    const Picture = () => (
        <picture>
            <source
                media="(min-width: 480px)"
                srcSet={isRebranded ? heroImgToUse.desktop : url}
            />
            <img
                src={isRebranded ? heroImgToUse.mobile : mobileUrl}
                alt={isRebranded ? rebrandedAlt : alt}
            />
        </picture>
    );
    return (
        <>
            <ValuePropBanner
                {...valuePropBannerProps}
            />
            <section
                id="ulx-hero"
            >
                <div className={cn('ulx__aem-hero__background',
                    { redesigned: (isFrboFlow || isRebranded) })}
                >
                    <Picture />
                    <div className={cn('mask')} />
                </div>
                <div
                    className={cn('ulx__aem-hero',
                        { 'ulx__aem-hero-redesigned': (isFrboFlow || isRebranded) })}
                >
                    <HeroV4
                        display={isRebranded}
                        {...heroV4Props}
                    />
                    <HeroV1
                        display={!isRebranded && !isFrboFlow}
                        heroCopy={heroCopy}
                        logoInfo={logoInfo}
                    />
                    <HeroV2
                        display={!isRebranded && isFrboFlow}
                        copy={heroV2Props.copy}
                        titleList={heroV2Props.titleList}
                        titleSuffix={heroV2Props.titleSuffix}
                    />
                    <div
                        className={cn('ulx__aem-wizard')}
                        id="list-your-property-form"
                    >
                        <section>
                            <Card elevation="0" theme="default">
                                <CardContent
                                    collapsePadding={false}
                                    separatorBottom={false}
                                    separatorTop={false}
                                >
                                    {children}
                                </CardContent>
                            </Card>
                        </section>
                    </div>
                </div>
            </section>
        </>
    );
};

function mapStateToProps(state) {
    const { app } = state;
    const {
        pointOfSale,
        features,
        footerLinks,
        displayBrand
    } = app;
    const translations = new TranslationUtil(
        app.translations,
        app.fallbackTranslations
    );
    const experimentsUtil = new ExperimentUtil(app.experiments);
    const contentMessagingUpdatesEnabled = isContentMessagingUpdatesEnabled(experimentsUtil);
    const { isFrboFlow } = features;
    let { logoInfo } = pointOfSale;
    if (isFrboFlow && footerLinks) {
        logoInfo = {
            link: footerLinks?.logo?.image || '',
            altText: footerLinks?.logo?.altText || ''
        };
    }
    const heroV2Props = {
        copy: translations.getTranslation('Vrbo.lyp.hero.copy.V2', { brand: displayBrand }),
        titleSuffix: translations.getTranslation('Vrbo.lyp.hero.title.suffix.V2', { brand: displayBrand }),
        titleList: [
            translations.getTranslation('Vrbo.lyp.hero.title1.V2'),
            translations.getTranslation('Vrbo.lyp.hero.title2.V2'),
            translations.getTranslation('Vrbo.lyp.hero.title3.V2')
        ]
    };

    const valuePropBannerProps = {
        show: isValuePropBannerActive(experimentsUtil),
        copy: isFrboFlow ? translations.getTranslation('Vrbo.valuePropBanner.text') : translations.getTranslation('CL.valuePropBanner.text')
    };

    return {
        valuePropBannerProps,
        logoInfo,
        contentMessagingUpdatesEnabled,
        translations,
        isFrboFlow,
        heroV2Props,
        displayBrand
    };
}
Hero.propTypes = {
    valuePropBannerProps: PropTypes.shape({
        show: PropTypes.bool.isRequired,
        copy: PropTypes.string.isRequired
    }).isRequired,
    image: PropTypes.shape({
        url: PropTypes.string.isRequired,
        alt: PropTypes.string
    }).isRequired,
    logoInfo: PropTypes.shape({
        link: PropTypes.string,
        altText: PropTypes.string
    }).isRequired,
    contentMessagingUpdatesEnabled: PropTypes.bool.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    translations: PropTypes.object.isRequired,
    copy: PropTypes.string.isRequired,
    isFrboFlow: PropTypes.bool.isRequired,
    children: PropTypes.oneOf([PropTypes.array, PropTypes.object]).isRequired,
    heroV2Props: PropTypes.shape({
        copy: PropTypes.string.isRequired,
        titleSuffix: PropTypes.string.isRequired,
        titleList: PropTypes.arrayOf(PropTypes.string).isRequired
    }).isRequired,
    isRebranded: PropTypes.bool.isRequired,
    displayBrand: PropTypes.string.isRequired
};
export default connect(mapStateToProps)(Hero);
